import React, { useContext, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { AuthStore, CommonStore, localesStore } from 'stores';
import { URLS, PATHS } from 'config';
import * as trackingData from 'tracking/trackingData';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import qs from 'query-string';
import * as help from 'helpers';
import Icon from 'components/Icon';
import Navigation, {
    NavigationDropdown,
    NavigationItem,
} from 'components/Navigation';

const Nav = ({
    analytics,
    history,
    location,
}: {
    analytics: any;
    history: RouteComponentProps['history'];
    location: RouteComponentProps['location'];
}) => {
    const [mobMenuClosed, setMobMenuClosed] = useState<boolean>(false);

    const auth = useContext(AuthStore);
    const common = useContext(CommonStore);
    const { user, subscription } = common;
    const { locale } = localesStore;
    const isAuthPage =
        [
            PATHS.login,
            PATHS.register,
            PATHS.access,
            PATHS.forgotPassword,
            PATHS.resetPassword,
            PATHS.confirmEmail,
        ].indexOf(location.pathname) >= 0;

    const isCampaignPage =
        [
            PATHS.freeTrialOffer,
            PATHS.freeTrialOffer7,
            PATHS.blackFriday,
            PATHS.blackFridayCheckout,
            PATHS.aprilDiscount,
            PATHS.aprilDiscountCheckout,
            PATHS.partnershipJason,
            PATHS.partnershipJasonCheckout,
            PATHS.yousician25,
            PATHS.yousician25Checkout,
            PATHS.premiumOffer,
            PATHS.premiumOfferCheckout,
            PATHS.premiumPlusOffer,
            PATHS.premiumPlusOfferCheckout,
            PATHS.worldMusicDay,
            PATHS.worldMusicDayCheckout,
            PATHS.newYear,
            PATHS.newYearCheckout,
            PATHS.backToSchool,
            PATHS.backToSchoolCheckout,
        ].indexOf(location.pathname) >= 0;

    if (!common.showNavigation) {
        return null;
    }

    const localeToLanguage = [
        { locale: 'en', language: 'English' },
        { locale: 'fr', language: 'Français' },
        { locale: 'de', language: 'Deutsch' },
        { locale: 'es-419', language: 'Español' },
        { locale: 'it', language: 'Italiano' },
        { locale: 'nl', language: 'Nederlands' },
        { locale: 'pt-BR', language: 'Português' },
        { locale: 'ru', language: 'Русский' },
        { locale: 'ja', language: '日本語' },
        { locale: 'zh-Hans', language: '简体中文' },
        { locale: 'zh-Hant', language: '繁體中文' },
    ];

    const onChangeLocale = async (newLocale: string) => {
        setMobMenuClosed(true);
        localesStore.pseudo_locale = newLocale === 'pseudo-LOCALE';
        if (user) {
            await localesStore.setUserLocale(newLocale);
        } else {
            let params = qs.parse(window.location.search);
            params.locale = newLocale;
            help.goto(window.location.pathname, params);
        }
        window.location.reload();
    };

    const getLocaleLabel = (): string => {
        const item = localeToLanguage.find((o) => o.locale === locale);
        return item ? item.language : '';
    };

    const navigationProps = {
        logoProps: {
            href: user ? PATHS.dashboard : URLS.yousician,
            onClick: (e: Event) => {
                if (user) e && e.preventDefault();
                setMobMenuClosed(true);
                history.push(PATHS.dashboard);
            },
        },
        showUser: true,
        menu: (
            <React.Fragment key="main-menu">
                <NavigationDropdown label={getLocaleLabel()}>
                    {localeToLanguage.map((x) => {
                        return (
                            <NavigationItem
                                key={x.language}
                                href="#"
                                onClick={(e) => {
                                    e && e.preventDefault();
                                    setMobMenuClosed(true);
                                    onChangeLocale(x.locale);
                                }}
                            >
                                {locale === x.locale && (
                                    <Icon size={15} name="checkmark" />
                                )}
                                &nbsp;{x.language}
                            </NavigationItem>
                        );
                    })}
                    {window.yconfig.Env !== 'prod' && (
                        <NavigationItem
                            href="#"
                            onClick={(e) => {
                                e && e.preventDefault();
                                setMobMenuClosed(true);
                                onChangeLocale('pseudo-LOCALE');
                            }}
                        >
                            &nbsp;Pseudo Localisation
                        </NavigationItem>
                    )}
                </NavigationDropdown>
                <NavigationItem
                    onClick={(e) => {
                        e && e.preventDefault();
                        setMobMenuClosed(true);

                        analytics.trackEvent(
                            trackingData.NAVIGATION__LINK__SUPPORT,
                        );
                        window.location.href = URLS.support;
                    }}
                    href={URLS.support}
                >
                    <Trans>Support</Trans>
                </NavigationItem>
                <NavigationItem
                    onClick={(e) => {
                        e && e.preventDefault();
                        setMobMenuClosed(true);

                        analytics.trackEvent(
                            trackingData.NAVIGATION__LINK__DOWNLOAD,
                        );
                        history.push(PATHS.download);
                    }}
                    href={PATHS.download}
                >
                    <Trans>Download</Trans>
                </NavigationItem>
            </React.Fragment>
        ),
        fixed: true,
        light: false,
        showLogo: true,
        profileLabel: i18n._(t`Account`),
        loginLabel: i18n._(t`Login`),
    };

    if (common.user && common.user.is_silent) {
        return (
            <Navigation
                {...navigationProps}
                closeMenu={mobMenuClosed}
                onMenuOpen={() => setMobMenuClosed(false)}
                user={{
                    avatar: user.avatar,
                    menu: (
                        <NavigationItem
                            href="#"
                            active={location.pathname === PATHS.dashboard}
                            label={i18n._(t`Dashboard`)}
                            onClick={(e) => {
                                e && e.preventDefault();
                                setMobMenuClosed(true);
                                history.push(PATHS.dashboard);
                            }}
                        />
                    ),
                }}
            />
        );
    }

    return (
        <Navigation
            {...navigationProps}
            closeMenu={mobMenuClosed}
            onMenuOpen={() => setMobMenuClosed(false)}
            cta={
                subscription || isAuthPage || isCampaignPage
                    ? null
                    : {
                          props: {
                              onClick: (e: Event) => {
                                  e && e.preventDefault();
                                  setMobMenuClosed(true);
                                  analytics.trackEvent({
                                      ...trackingData.NAVIGATION__BTN__GO_PREMIUM,
                                  });
                                  help.is_campaign_active('black-friday')
                                      ? history.push(PATHS.blackFriday)
                                      : history.push(PATHS.subscribe);
                              },
                          },
                          label: i18n._(t`Go premium`),
                      }
            }
            loginButtonProps={{
                onClick: (e: Event) => {
                    e && e.preventDefault();
                    setMobMenuClosed(true);
                    analytics.trackEvent({
                        ...trackingData.NAVIGATION__BTN__LOGIN,
                    });
                    history.push(PATHS.login);
                },
            }}
            user={
                user
                    ? {
                          avatar: user.avatar,
                          menu: (
                              <React.Fragment>
                                  <NavigationItem
                                      href="#"
                                      data-testid="account"
                                      active={
                                          location.pathname === PATHS.dashboard
                                      }
                                      label={i18n._(t`My account`)}
                                      onClick={(e) => {
                                          e && e.preventDefault();
                                          setMobMenuClosed(true);

                                          analytics.trackEvent({
                                              ...trackingData.NAVIGATION__LINK__DROPDOWN_ITEMS,
                                              name: 'my-account',
                                          });

                                          history.push(PATHS.dashboard);
                                      }}
                                  />
                                  <NavigationItem
                                      href="#"
                                      data-testid="coupon"
                                      active={
                                          location.pathname === PATHS.redeem
                                      }
                                      label={i18n._(t`Apply discount code`)}
                                      onClick={(e) => {
                                          e && e.preventDefault();
                                          setMobMenuClosed(true);

                                          analytics.trackEvent({
                                              ...trackingData.NAVIGATION__LINK__DROPDOWN_ITEMS,
                                              name: 'redeem-coupon',
                                          });

                                          history.push(PATHS.redeem);
                                      }}
                                  />
                                  <NavigationItem
                                      href="#"
                                      data-testid="gift"
                                      active={
                                          location.pathname === PATHS.giftRedeem
                                      }
                                      label={i18n._(t`Redeem gift card`)}
                                      onClick={(e) => {
                                          e && e.preventDefault();
                                          setMobMenuClosed(true);

                                          analytics.trackEvent({
                                              ...trackingData.NAVIGATION__LINK__DROPDOWN_ITEMS,
                                              name: 'redeem-gift-card',
                                          });

                                          history.push(PATHS.giftRedeem);
                                      }}
                                  />
                                  <NavigationItem
                                      href="#"
                                      data-testid="logout"
                                      label={i18n._(t`Logout`)}
                                      onClick={(e) => {
                                          e && e.preventDefault();
                                          setMobMenuClosed(true);
                                          analytics.trackEvent({
                                              ...trackingData.NAVIGATION__BTN__LOGOUT,
                                          });
                                          auth.logout();
                                          history.replace(PATHS.login);
                                      }}
                                  />
                              </React.Fragment>
                          ),
                      }
                    : null
            }
        />
    );
};

export default observer(Nav);
